import * as z from "zod/v4";
import superjson from "superjson";
import type * as zcore from "zod/v4/core";

/** sessionStorage counterpart of the localStorage helpers, for values that must
 *  survive a full-page round trip but must not outlive the tab.
 */
export function setSessionStorageValue<TInput, TOutput = TInput>(
  key: string,
  value: TInput,
  schema?: zcore.$ZodType<TOutput, TInput>,
): void {
  const v = schema ? z.parse(schema, value) : value;
  sessionStorage.setItem(key, superjson.stringify(v));
}

/** Values failing validation are cleared, matching getLocalStorageValue */
export function getSessionStorageValue<T>(
  key: string,
  schema?: zcore.$ZodType<T>,
): T | null {
  const value = sessionStorage.getItem(key);
  if (value === null) return null;

  if (!schema) return superjson.parse<T>(value);

  const parsed = z.safeParse(schema, superjson.parse(value));
  if (!parsed.success) {
    sessionStorage.removeItem(key);
    return null;
  }
  return parsed.data;
}

export function removeSessionStorageValue(key: string): void {
  sessionStorage.removeItem(key);
}

/** Reads a value and clears it, for one-shot handoffs */
export function takeSessionStorageValue<T>(
  key: string,
  schema?: zcore.$ZodType<T>,
): T | null {
  const value = getSessionStorageValue(key, schema);
  sessionStorage.removeItem(key);
  return value;
}
